
import { useEffect, useState } from 'react';
import { cn } from '@jk-core/utils';
import CloseIcon from '@/assets/close.svg';
import styles from './Calendar.module.scss';
import DateLabel from './components/DateLabel';
import DayTile from './components/DayTile';
import MonthTile from './components/MonthTile';
import ViewSelector from './components/ViewSelector';
import YearTile from './components/YearTile';
import useCalendarNav from './hooks/useCalendarNav';
import useDateSelect from './hooks/useDateSelect';
import { CalendarView } from './type';
import getWeeksInMonth from './utils/getWeeksInMonth';
import './index.scss';

interface CalendarProps {
  className?: string;
  date?: Date;
  view?: CalendarView;
  setView?: (view:CalendarView)=>void;
  tileContent?: (date: Date | undefined, view: CalendarView) => React.ReactNode;
  onChange?:(date:Date, method?:CalendarView)=>void;
  min?: Date;
  max?: Date;
  onClose?: () => void;
  viewSelector?: boolean;
}

export default function SingleCalendar({
  className = '', date: selectedDate, view, setView = () => { }, tileContent, onChange = () => { }, onClose, viewSelector = true,
  min = new Date(2000, 0, 1), max = new Date(2099, 11, 31),
}: CalendarProps) {
  const [viewDate, setViewDate] = useState<Date>(selectedDate || new Date());
  const [method, setMethod] = useState<CalendarView>(view || 'day');
  const [selectMode, setSelectMode] = useState<CalendarView>(view || 'day');
  const weeksInMonth = getWeeksInMonth(viewDate);
  const { onDayClick, onMonthClick, onYearClick } = useDateSelect({
    viewDate,
    setViewDate: (data) => setViewDate(data),
    onChange,
    setSelectMode,
    method,
    setView,
  });
  const { disabled, onArrowClick } = useCalendarNav({
    method, selectMode, viewDate, setDate: setViewDate, min, max,
  });

  const selectView = (value: CalendarView) => {
    setMethod(value);
    setSelectMode(value);
  };

  useEffect(() => {
    if (viewDate > max) {
      setViewDate(max);
    }
    if (viewDate < min) {
      setViewDate(min);
    }
  }, [viewDate, max, min]);

  return (
    <div className={cn({
      [styles.calendar]: true,
      [className]: !!className,
    })}
    >
      <div className={styles.calendar__close}>
        {onClose && (
          <CloseIcon onClick={onClose} />
        )}
      </div>
      {/* 일/월/년 선택 버튼 */}
      {viewSelector && (
        <ViewSelector method={method} selectView={selectView} />
      )}
      <DateLabel
        viewDate={viewDate}
        method={method}
        onArrowClick={onArrowClick}
        disabled={disabled}
        selectMode={selectMode}
        setSelectMode={setSelectMode}
      />

      {(method === 'day' && selectMode === 'day') && (
        <DayTile
          selectedDate={[selectedDate, null]}
          weeksInMonth={weeksInMonth}
          handleDayClick={onDayClick}
          tileContent={tileContent}
          max={max}
          min={min}
        />
      )}

      {((method === 'month' || selectMode === 'month') && selectMode !== 'year') && (
        <MonthTile
          selectedDate={selectedDate}
          viewDate={viewDate}
          handleMonthClick={onMonthClick}
          tileContent={tileContent}
          max={max}
          min={min}
        />
      )}

      {(method === 'year' || selectMode === 'year') && (
        <YearTile
          selectedDate={selectedDate}
          onClick={onYearClick}
          tileContent={tileContent}
          max={max}
          min={min}
        />
      )}
    </div>
  );
}
